import { useEffect, useState } from 'preact/hooks';
import type { Api } from './api.js';
import { h } from './format.js';
import { texts, useLang, type Lang } from './i18n.js';
import { Dialog, MoreOptions, toast } from './ui.js';
import type { MyWeekView, WeekItem } from './pages/WeekCalendar.js';

/**
 * "Planera tid" (owner 2026-11-02/03): a block of time on one of your projects, from the week calendar on My work, the
 * project list's ⋯ and the project page. The block is a plan, never busy time: its copy in your Google Calendar is
 * "free", so meetings can still be booked over it. Producers see the blocks in Team and on the project page.
 */
export interface PlanBlockView {
  id: string;
  personId: string;
  person: string;
  projectId: string;
  project: string;
  client: string;
  videoId: string | null;
  video: string | null;
  kind: 'edit' | 'camera';
  day: string;
  start: string;
  end: string;
  hours: number;
  note: string;
  google: 'off' | 'sent' | 'error';
  mine: boolean;
}

/** What opens the dialog: a day from the calendar, a project from a list, or a block to move. */
export interface OpenPlan {
  day?: string;
  projectId?: string;
  block?: { id: string; projectId: string; videoId: string | null; day: string; start: string; end: string; note: string; google: 'off' | 'sent' | 'error' };
}
const OPEN = 'kff-plan-open';
const CHANGED = 'kff-plan-changed';
export const openPlan = (o: OpenPlan = {}) => dispatchEvent(new CustomEvent<OpenPlan>(OPEN, { detail: o }));
export const planChanged = () => dispatchEvent(new Event(CHANGED));
export const onPlanChanged = (fn: () => void) => {
  addEventListener(CHANGED, fn);
  return () => removeEventListener(CHANGED, fn);
};

export const PT = texts(
  {
    title: 'Plan time',
    editTitle: 'Planned time',
    menu: 'Plan time',
    project: 'Project',
    choose: 'Choose a project',
    day: 'Day',
    from: 'From',
    to: 'To',
    allDay: 'Whole day',
    google: 'Add to my Google Calendar',
    googleHint: 'Shown as free, so meetings can still be booked.',
    googleError: 'Could not be added to Google Calendar. It is planned in the Hub.',
    note: 'Note',
    video: 'Video',
    anyVideo: 'The whole project',
    alsoOn: 'Same time on more days',
    free: (a: string, b: string) => `Free in your calendar ${a}–${b}`,
    clash: (what: string) => `Overlaps: ${what}`,
    cancel: 'Cancel',
    save: 'Plan',
    saveEdit: 'Save',
    saving: 'Saving…',
    remove: 'Remove',
    planned: (n: number, what: string) => (n > 1 ? `Planned ${n} days on ${what}` : `Planned time on ${what}`),
    moved: 'Planned time moved',
    removed: 'Planned time removed',
    badTime: 'The end must be after the start',
    dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    plannedLabel: (hours: string, days: string) => `Planned ${hours} · ${days}`,
    nonePlanned: 'No time planned',
    whoPlanned: 'Planned time',
    nobodyPlanned: 'Nobody has planned time on the project yet.',
    planBtn: '+ Plan time',
    notBusy: 'Planned time is not busy time: meetings can be booked over it.'
  },
  {
    title: 'Planera tid',
    editTitle: 'Planerad tid',
    menu: 'Planera tid',
    project: 'Projekt',
    choose: 'Välj ett projekt',
    day: 'Dag',
    from: 'Från',
    to: 'Till',
    allDay: 'Heldag',
    google: 'Lägg i min Google-kalender',
    googleHint: 'Visas som ledig tid, så möten kan bokas ändå.',
    googleError: 'Kunde inte läggas i Google-kalendern. Den är planerad i Hubben.',
    note: 'Anteckning',
    video: 'Video',
    anyVideo: 'Hela projektet',
    alsoOn: 'Samma tid fler dagar',
    free: (a: string, b: string) => `Ledigt i din kalender ${a}–${b}`,
    clash: (what: string) => `Krockar med: ${what}`,
    cancel: 'Avbryt',
    save: 'Planera',
    saveEdit: 'Spara',
    saving: 'Sparar…',
    remove: 'Ta bort',
    planned: (n: number, what: string) => (n > 1 ? `Planerade ${n} dagar på ${what}` : `Planerade tid på ${what}`),
    moved: 'Planerad tid flyttad',
    removed: 'Planerad tid borttagen',
    badTime: 'Slutet måste vara efter starten',
    dow: ['sön', 'mån', 'tis', 'ons', 'tor', 'fre', 'lör'],
    months: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
    plannedLabel: (hours: string, days: string) => `Planerat ${hours} · ${days}`,
    nonePlanned: 'Ingen tid planerad',
    whoPlanned: 'Planerad tid',
    nobodyPlanned: 'Ingen har planerat tid på projektet än.',
    planBtn: '+ Planera tid',
    notBusy: 'Planerad tid är inte upptagen tid: möten kan bokas över den.'
  }
);

const dt = (k: string) => new Date(`${k}T12:00:00Z`);
const addDays = (k: string, n: number) => new Date(dt(k).getTime() + n * 86400_000).toISOString().slice(0, 10);
const mondayOf = (k: string) => addDays(k, -((dt(k).getUTCDay() + 6) % 7));
const todayKey = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm' }).format(new Date());
const min = (t: string) => +t.slice(0, 2) * 60 + +t.slice(3, 5);
const hhmm = (m: number) => (m >= 1440 ? '24:00' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
/** "mån 9 nov" */
export const dayLabel = (k: string, lang: Lang) => `${PT[lang].dow[dt(k).getUTCDay()]} ${+k.slice(8, 10)} ${PT[lang].months[+k.slice(5, 7) - 1]}`;
/** "mån, ons" for the label on a project row */
export const daysShort = (days: string[], lang: Lang) => days.map((k) => PT[lang].dow[dt(k).getUTCDay()]).join(', ');

/** Timed things on the day that a block could overlap (Google events, shoots, other planned blocks). */
function taken(items: WeekItem[], skip?: string): { from: string; to: string; what: string }[] {
  const out: { from: string; to: string; what: string }[] = [];
  for (const it of items) {
    if (it.kind === 'busy' && !it.allDay) out.push({ from: it.from, to: it.to, what: `${it.title ?? ''} ${it.from}–${it.to}`.trim() });
    else if (it.kind === 'plan' && it.id !== skip) out.push({ from: it.from, to: it.to, what: `${it.title} ${it.from}–${it.to}` });
    else if (it.kind === 'shoot' && it.booked) out.push({ from: it.booked.from, to: it.booked.to, what: `${it.title} ${it.booked.from}–${it.booked.to}` });
  }
  return out;
}

/** The first free stretch of `len` minutes between 09 and 17 (on the half hour), else 09:00. */
export function firstFree(items: WeekItem[], len: number): { start: string; end: string } {
  const busy = taken(items).map((x) => [min(x.from), min(x.to)] as const);
  for (let s = 9 * 60; s + len <= 17 * 60; s += 30) if (!busy.some(([a, b]) => s < b && s + len > a)) return { start: hhmm(s), end: hhmm(s + len) };
  return { start: '09:00', end: hhmm(9 * 60 + len) };
}

interface Choice {
  projectId: string;
  projectName: string;
  client: string;
}

/** Mounted once in the app: any page opens the dialog with openPlan(). */
export function PlanHost(p: { api: Api }) {
  const [open, setOpen] = useState<OpenPlan | null>(null);
  useEffect(() => {
    const on = (e: Event) => setOpen((e as CustomEvent<OpenPlan>).detail ?? {});
    addEventListener(OPEN, on);
    return () => removeEventListener(OPEN, on);
  }, []);
  return open ? <PlanDialog api={p.api} o={open} onClose={() => setOpen(null)} /> : null;
}

function PlanDialog(p: { api: Api; o: OpenPlan; onClose: () => void }) {
  const lang = useLang();
  const t = PT[lang];
  const b = p.o.block;
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [projectId, setProjectId] = useState(b?.projectId ?? p.o.projectId ?? '');
  const [videos, setVideos] = useState<{ id: string; name: string }[]>([]);
  const [videoId, setVideoId] = useState(b?.videoId ?? '');
  const [day, setDay] = useState(b?.day ?? p.o.day ?? todayKey());
  const [start, setStart] = useState(b?.start ?? '');
  const [end, setEnd] = useState(b?.end ?? '');
  const [note, setNote] = useState(b?.note ?? '');
  const [google, setGoogle] = useState(b ? b.google !== 'off' : true);
  const [more, setMore] = useState<string[]>([]);
  const [week, setWeek] = useState<MyWeekView | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // your projects first (the same list as the timer's), with the one asked for kept even when it is not yours
  useEffect(() => {
    p.api.get<{ mine: Choice[] }>('/api/time/projects').then(
      (r) => {
        setChoices(r.mine);
        // the usual pick ready: your most recent project (or the one due first)
        setProjectId((cur) => cur || r.mine[0]?.projectId || '');
      },
      () => setChoices([])
    );
  }, []);
  // the week of the day: what is already on it (for the free-time hint) and whether Google can take the copy
  useEffect(() => {
    let live = true;
    p.api.get<MyWeekView>(`/api/my-week?week=${mondayOf(day)}`).then(
      (w) => {
        if (!live) return;
        setWeek(w);
        if (!b && !start) {
          const items = w.days.find((d) => d.date === day)?.items ?? [];
          const f = firstFree(items, 120);
          setStart(f.start);
          setEnd(f.end);
        }
      },
      () => live && setWeek(null)
    );
    return () => {
      live = false;
    };
  }, [mondayOf(day)]);
  useEffect(() => {
    if (!projectId) return setVideos([]);
    p.api.get<{ videos: { id: string; name: string; step: string }[] }>(`/api/projects/${projectId}`).then(
      (v) => setVideos(v.videos.filter((x) => x.step !== 'cancelled' && x.step !== 'delivered').map((x) => ({ id: x.id, name: x.name }))),
      () => setVideos([])
    );
  }, [projectId]);
  const items = week?.days.find((d) => d.date === day)?.items ?? [];
  const valid = /^\d\d:\d\d$/.test(start) && /^\d\d:\d\d$/.test(end) && min(end) > min(start);
  const clash = valid ? taken(items, b?.id).filter((x) => min(start) < min(x.to) && min(end) > min(x.from)) : [];
  const googleOn = !!week?.plan?.google;
  const len = valid ? min(end) - min(start) : 120;
  const setLen = (m: number) => {
    const s = start && /^\d\d:\d\d$/.test(start) ? min(start) : 9 * 60;
    setStart(hhmm(s));
    setEnd(hhmm(Math.min(s + m, 1440)));
  };
  const list = choices ?? [];
  const shown = projectId && !list.some((c) => c.projectId === projectId) ? [{ projectId, projectName: '…', client: '' }, ...list] : list;
  const name = list.find((c) => c.projectId === projectId)?.projectName ?? '';
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(mondayOf(day), i)).filter((k) => k !== day && k >= todayKey());
  const save = async () => {
    if (!projectId) return setErr(t.choose);
    if (!valid) return setErr(t.badTime);
    setBusy(true);
    setErr('');
    try {
      const body = { projectId, videoId: videoId || null, start, end, note, google: googleOn ? google : undefined };
      if (b) {
        const r = await p.api.put<{ block: PlanBlockView }>(`/api/plan/${b.id}`, { ...body, day });
        toast({ text: r.block?.google === 'error' ? t.googleError : t.moved });
      } else {
        const r = await p.api.post<{ blocks: PlanBlockView[] }>('/api/plan', { ...body, days: [day, ...more] });
        toast({ text: r.blocks.some((x) => x.google === 'error') ? t.googleError : t.planned(r.blocks.length, name) });
      }
      planChanged();
      p.onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!b) return;
    setBusy(true);
    try {
      await p.api.del(`/api/plan/${b.id}`);
      toast({ text: t.removed });
      planChanged();
      p.onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={b ? t.editTitle : t.title}
      sub={dayLabel(day, lang)}
      onClose={p.onClose}
      onSubmit={() => void save()}
      foot={
        <>
          {b ? (
            <button type="button" class="btn btn-ghost pt-remove" disabled={busy} onClick={() => void remove()}>
              {t.remove}
            </button>
          ) : null}
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={busy}>
            {busy ? t.saving : b ? t.saveEdit : t.save}
          </button>
        </>
      }
    >
      <div class="field">
        <label class="label" for="pt-project">
          {t.project}
        </label>
        <select class="input input--wide" id="pt-project" value={projectId} onChange={(e) => (setProjectId((e.target as HTMLSelectElement).value), setVideoId(''))}>
          <option value="">{choices ? t.choose : '…'}</option>
          {shown.map((c) => (
            <option value={c.projectId}>{c.client ? `${c.projectName} · ${c.client}` : c.projectName}</option>
          ))}
        </select>
        {list.length > 1 ? (
          <div class="pt-chips" role="group" aria-label={t.project}>
            {list.slice(0, 4).map((c) => (
              <button type="button" class="chip chip--sm" aria-pressed={c.projectId === projectId} onClick={() => (setProjectId(c.projectId), setVideoId(''))}>
                {c.client || c.projectName}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div class="pt-row">
        <div class="field">
          <label class="label" for="pt-day">
            {t.day}
          </label>
          <input class="input input--date" type="date" id="pt-day" value={day} onInput={(e) => (setDay((e.target as HTMLInputElement).value), setMore([]))} />
        </div>
        <div class="field">
          <label class="label" for="pt-from">
            {t.from}
          </label>
          <input class="input pt-time" type="time" step={900} id="pt-from" value={start} onInput={(e) => setStart((e.target as HTMLInputElement).value)} />
        </div>
        <div class="field">
          <label class="label" for="pt-to">
            {t.to}
          </label>
          <input class="input pt-time" type="time" step={900} id="pt-to" value={end === '24:00' ? '23:59' : end} onInput={(e) => setEnd((e.target as HTMLInputElement).value)} />
        </div>
      </div>
      <div class="pt-chips" role="group" aria-label={t.to}>
        {[60, 120, 180].map((m) => (
          <button type="button" class="chip chip--sm" aria-pressed={len === m} onClick={() => setLen(m)}>
            {h(m / 60)}
          </button>
        ))}
        <button type="button" class="chip chip--sm" aria-pressed={start === '09:00' && end === '17:00'} onClick={() => (setStart('09:00'), setEnd('17:00'))}>
          {t.allDay}
        </button>
      </div>
      {valid && week?.calendar === 'ok' ? (
        clash.length ? (
          <p class="pt-hint pt-hint--warn">{t.clash(clash.map((x) => x.what).join(', '))}</p>
        ) : (
          <p class="pt-hint pt-hint--ok">{t.free(start, end)}</p>
        )
      ) : clash.length ? (
        <p class="pt-hint pt-hint--warn">{t.clash(clash.map((x) => x.what).join(', '))}</p>
      ) : null}
      {googleOn ? (
        <label class="check">
          <input type="checkbox" checked={google} onChange={(e) => setGoogle((e.target as HTMLInputElement).checked)} />
          <span>
            {t.google}
            <span class="small muted"> · {t.googleHint}</span>
          </span>
        </label>
      ) : null}
      <MoreOptions id="plan-time" filled={(note ? 1 : 0) + (videoId ? 1 : 0) + more.length}>
        <div class="field">
          <label class="label" for="pt-note">
            {t.note}
          </label>
          <input class="input input--wide" id="pt-note" maxLength={500} value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)} />
        </div>
        {videos.length > 1 ? (
          <div class="field">
            <label class="label" for="pt-video">
              {t.video}
            </label>
            <select class="input input--wide" id="pt-video" value={videoId} onChange={(e) => setVideoId((e.target as HTMLSelectElement).value)}>
              <option value="">{t.anyVideo}</option>
              {videos.map((v) => (
                <option value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
        ) : null}
        {!b && weekDays.length ? (
          <fieldset class="pt-days">
            <legend class="label">{t.alsoOn}</legend>
            {weekDays.map((k) => (
              <label class="check">
                <input type="checkbox" checked={more.includes(k)} onChange={(e) => setMore((e.target as HTMLInputElement).checked ? [...more, k].sort() : more.filter((x) => x !== k))} />
                <span>{dayLabel(k, lang)}</span>
              </label>
            ))}
          </fieldset>
        ) : null}
      </MoreOptions>
      {err ? (
        <p class="field-error" role="alert">
          {err}
        </p>
      ) : null}
    </Dialog>
  );
}

/**
 * The project's planned time (owner 2026-11-03: producers see the blocks), on the project page under "Vad händer nu":
 * who planned what the coming weeks, and "+ Planera tid" for the people on the project.
 */
export function ProjectPlan(p: { api: Api; projectId: string; canPlan: boolean }) {
  const lang = useLang();
  const t = PT[lang];
  const [v, setV] = useState<{ today: string; blocks: PlanBlockView[] } | null>(null);
  const load = () =>
    p.api.get<{ today: string; blocks: PlanBlockView[] }>(`/api/projects/${p.projectId}/plan`).then(
      // an older API answers without the list: nothing to show
      (x) => setV(Array.isArray(x?.blocks) ? x : null),
      () => setV(null)
    );
  useEffect(() => {
    void load();
    return onPlanChanged(() => void load());
  }, [p.projectId]);
  if (!v) return null;
  const coming = v.blocks.filter((b) => b.day >= v.today);
  if (!coming.length && !p.canPlan) return null;
  return (
    <section class="panel pt-panel" aria-labelledby="pt-h" data-tour="project-plan">
      <header class="pt-panel-h">
        <h2 class="blk-title" id="pt-h">
          {t.whoPlanned}
        </h2>
        {p.canPlan ? (
          <button type="button" class="btn btn-sm" onClick={() => openPlan({ projectId: p.projectId })}>
            {t.planBtn}
          </button>
        ) : null}
      </header>
      {coming.length ? (
        <ul class="pt-list">
          {coming.map((b) => (
            <li>
              {b.mine ? (
                <button type="button" class="pt-link" onClick={() => openPlan({ block: b })}>
                  {dayLabel(b.day, lang)} {b.start}–{b.end}
                </button>
              ) : (
                <span>
                  {dayLabel(b.day, lang)} {b.start}–{b.end}
                </span>
              )}
              <span class="muted">
                {' · '}
                {b.person}
                {b.video ? ` · ${b.video}` : ''}
                {b.note ? ` · ${b.note}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p class="small muted">{t.nobodyPlanned}</p>
      )}
      <p class="small muted pt-foot">{t.notBusy}</p>
    </section>
  );
}
